import React, { useEffect, useState, useCallback } from 'react';
import { Table, Input, Breadcrumb, DatePicker, Button, Tag, Grid, Row, Col, Card, Statistic, Avatar, Tooltip, Dropdown, message, Select } from 'antd';
import { Link } from 'react-router-dom';
import {
  RightOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  DownloadOutlined,
  SearchOutlined,
  CarOutlined,
  CalendarOutlined,
  TagOutlined,
  AppstoreOutlined,
  EyeOutlined,
  EditOutlined,
  MoreOutlined,
  EnvironmentOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { vehiculesServices, citiesService } from '../../common/services';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

dayjs.extend(customParseFormat);

// Couleurs pour les catégories
const CATEGORY_COLORS = {
  'econom': '#52c41a',
  'comfort': '#1890ff',
  'comfort_plus': '#722ed1',
  'business': '#fa8c16',
  'start': '#13c2c2',
  'standart': '#2f54eb',
  'moto': '#eb2f96',
  'intercity': '#faad14',
  'courier': '#f5222d',
  'express': '#fa541c',
  'pool': '#a0d911',
  'minivan': '#597ef7',
};

const getCategoryColor = (category) => {
  const cat = category?.toLowerCase();
  return CATEGORY_COLORS[cat] || '#8c8c8c';
};

// Formater la date
const formatDate = (date) => {
  if (!date) return '-';
  const formats = [
    'YYYY-MM-DDTHH:mm:ss.SSSZ',
    'YYYY-MM-DD HH:mm:ss',
    'DD/MM/YYYY HH:mm:ss',
    'DD/MM/YYYY HH:mm',
    'DD/MM/YYYY',
  ];
  let parsed = dayjs(date);
  if (!parsed.isValid()) {
    for (const fmt of formats) {
      parsed = dayjs(date, fmt);
      if (parsed.isValid()) break;
    }
  }
  if (parsed.isValid()) {
    return parsed.format('DD/MM/YYYY HH:mm');
  }
  if (typeof date === 'string') return date;
  return '-';
};

const Vehicules = () => {
  const navigate = useNavigate();
  const [vehicules, setVehicules] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedCity, setSelectedCity] = useState(undefined);
  const [cities, setCities] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    categories: 0,
    thisMonth: 0,
    today: 0
  });

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const controlSize = isMobile ? 'small' : 'middle';

  // Calcul des statistiques
  const calculateStats = useCallback((data) => {
    const today = dayjs().startOf('day');
    const startOfMonth = dayjs().startOf('month');

    const todayCount = data.filter(item => {
      const itemDate = dayjs(item.createdAt, 'DD/MM/YYYY HH:mm:ss');
      return itemDate.isValid() && itemDate.isAfter(today);
    }).length;

    const monthCount = data.filter(item => {
      const itemDate = dayjs(item.createdAt, 'DD/MM/YYYY HH:mm:ss');
      return itemDate.isValid() && itemDate.isAfter(startOfMonth);
    }).length;

    // Compter les catégories uniques
    const allCategories = new Set();
    data.forEach(item => {
      (item.categories || []).forEach(cat => allCategories.add(cat));
    });

    setStats({
      total: data.length,
      categories: allCategories.size,
      thisMonth: monthCount,
      today: todayCount
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    vehiculesServices.recupererListeVehicules()
      .then((data) => {
        const vehiculesData = Array.isArray(data) ? data : [];
        setVehicules(vehiculesData);
        calculateStats(vehiculesData);
      })
      .catch((err) => console.error("Erreur lors du chargement des véhicules :", err))
      .finally(() => setLoading(false));
  }, [calculateStats]);

  // Charger les villes actives au montage
  useEffect(() => {
    const loadCities = async () => {
      try {
        const data = await citiesService.getAll();
        const allCities = Array.isArray(data) ? data : (data.data || []);
        const activeCities = allCities.filter(city => city.isActive === true);
        setCities(activeCities);
      } catch (error) {
        console.error('Erreur chargement villes:', error);
      }
    };
    loadCities();
  }, []);

  const filteredData = vehicules.filter((item) => {
    // Supprimer les espaces de la recherche pour matcher les immatriculations
    const search = searchTerm.replace(/\s+/g, '').toLowerCase();

    const matchText = !search ||
      item.marque?.toLowerCase().includes(search) ||
      item.modele?.toLowerCase().includes(search) ||
      item.immatriculation?.replace(/\s+/g, '').toLowerCase().includes(search) ||
      item.couleur?.toLowerCase().includes(search);

    const matchDate = selectedDate
      ? dayjs(item.createdAt, 'DD/MM/YYYY HH:mm:ss').format('YYYY-MM-DD') === dayjs(selectedDate).format('YYYY-MM-DD')
      : true;

    const matchCity = selectedCity
      ? item.city?._id === selectedCity
      : true;

    return matchText && matchDate && matchCity;
  });

  // Export Excel
  const exportToExcel = () => {
    const hideLoading = message.loading('Export Excel en cours...', 0);
    try {
      const worksheet = XLSX.utils.json_to_sheet(filteredData.map(item => ({
        Marque: item.marque,
        Modele: item.modele,
        Immatriculation: item.immatriculation,
        Couleur: item.couleur,
        Année: item.annee,
        Catégories: item.categories?.join(', '),
        Ville: item.city?.name || 'Non assignée',
        Date: item.createdAt,
      })));
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Véhicules");
      XLSX.writeFile(workbook, "vehicules_tigoon.xlsx");
      message.success(`${filteredData.length} véhicules exportés`);
    } catch (e) {
      message.error('Erreur lors de l\'export Excel');
    } finally {
      hideLoading();
    }
  };

  // Export CSV
  const exportToCSV = () => {
    const hideLoading = message.loading('Export CSV en cours...', 0);
    try {
      const worksheet = XLSX.utils.json_to_sheet(filteredData.map(item => ({
        Marque: item.marque,
        Modele: item.modele,
        Immatriculation: item.immatriculation,
        Couleur: item.couleur,
        Année: item.annee,
        Catégories: item.categories?.join(', '),
        Ville: item.city?.name || 'Non assignée',
        Date: item.createdAt,
      })));
      const csv = XLSX.utils.sheet_to_csv(worksheet);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, 'vehicules_tigoon.csv');
      message.success(`${filteredData.length} véhicules exportés`);
    } catch (e) {
      message.error('Erreur lors de l\'export CSV');
    } finally {
      hideLoading();
    }
  };

  // Export PDF
  const exportToPDF = () => {
    const hideLoading = message.loading('Export PDF en cours...', 0);
    try {
      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text("Liste des véhicules TIGOON", 14, 15);
      doc.setFontSize(10);
      doc.text(`Généré le ${dayjs().format('DD/MM/YYYY HH:mm')}`, 14, 22);

      autoTable(doc, {
        startY: 30,
        head: [['Marque', 'Modèle', 'Immatriculation', 'Couleur', 'Année', 'Catégories', 'Ville', 'Date']],
        body: filteredData.map(item => [
          item.marque,
          item.modele,
          item.immatriculation,
          item.couleur,
          item.annee,
          item.categories?.join(', '),
          item.city?.name || 'Non assignée',
          item.createdAt
        ]),
        styles: { fontSize: 8 },
        headStyles: { fillColor: [24, 144, 255] },
      });

      doc.save("vehicules_tigoon.pdf");
      message.success(`${filteredData.length} véhicules exportés`);
    } catch (e) {
      message.error('Erreur lors de l\'export PDF');
    } finally {
      hideLoading();
    }
  };

  // Menu dropdown pour export
  const exportMenuItems = [
    {
      key: 'excel',
      icon: <FileExcelOutlined style={{ color: '#52c41a' }} />,
      label: 'Exporter Excel (.xlsx)',
      onClick: exportToExcel
    },
    {
      key: 'csv',
      icon: <DownloadOutlined style={{ color: '#1890ff' }} />,
      label: 'Exporter CSV',
      onClick: exportToCSV
    },
    {
      key: 'pdf',
      icon: <FilePdfOutlined style={{ color: '#ff4d4f' }} />,
      label: 'Exporter PDF',
      onClick: exportToPDF
    }
  ];

  const columns = [
    {
      title: 'Véhicule',
      key: 'vehicule',
      width: isMobile ? 150 : 200,
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar
            size={36}
            style={{ backgroundColor: '#1890ff' }}
            icon={<CarOutlined />}
          />
          <div>
            <div style={{ fontWeight: 500, color: '#262626' }}>
              {record.marque} {record.modele}
            </div>
            <div style={{ fontSize: 12, color: '#8c8c8c' }}>
              {record.annee} - {record.couleur}
            </div>
          </div>
        </div>
      )
    },
    {
      title: 'Immatriculation',
      dataIndex: 'immatriculation',
      key: 'immatriculation',
      width: 140,
      sorter: (a, b) => a.immatriculation?.localeCompare(b.immatriculation),
      render: (immat) => (
        <Tag color="blue" style={{ fontWeight: 500, fontSize: 13 }}>
          {immat}
        </Tag>
      )
    },
    {
      title: 'Catégories',
      dataIndex: 'categories',
      key: 'categories',
      width: isMobile ? 150 : 300,
      render: (categories) => (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {(categories || []).map((cat, index) => (
            <Tag
              key={index}
              style={{
                backgroundColor: getCategoryColor(cat),
                color: 'white',
                border: 'none',
                fontWeight: 500,
                fontSize: 11,
                margin: 0
              }}
            >
              {cat.toUpperCase()}
            </Tag>
          ))}
        </div>
      ),
    },
    {
      title: 'Ville',
      dataIndex: 'city',
      key: 'city',
      width: 120,
      render: (city) => (
        <span style={{ color: '#595959' }}>
          <EnvironmentOutlined style={{ marginRight: 6, color: '#52c41a' }} />
          {city?.name || 'Non assignée'}
        </span>
      ),
      filters: [
        { text: 'Abidjan', value: 'Abidjan' },
        { text: 'San Pedro', value: 'San Pedro' },
        { text: 'Non assignée', value: null },
      ],
      onFilter: (value, record) => {
        if (value === null) return !record.city;
        return record.city?.name === value;
      },
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      sorter: (a, b) => {
        const aDate = dayjs(a.createdAt, 'DD/MM/YYYY HH:mm:ss');
        const bDate = dayjs(b.createdAt, 'DD/MM/YYYY HH:mm:ss');
        return aDate.valueOf() - bDate.valueOf();
      },
      render: (date) => (
        <Tooltip title={date}>
          <span style={{ color: '#595959' }}>
            <CalendarOutlined style={{ marginRight: 6, color: '#1890ff' }} />
            {formatDate(date)}
          </span>
        </Tooltip>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => {
        const actionItems = [
          {
            key: 'detail',
            icon: <EyeOutlined style={{ color: '#1890ff' }} />,
            label: 'Voir détails',
            onClick: () => navigate(`/dashboard/vehicule/${record._id}`)
          },
          {
            key: 'edit',
            icon: <EditOutlined style={{ color: '#52c41a' }} />,
            label: 'Modifier',
            onClick: () => navigate(`/dashboard/vehicule/${record._id}/modifier`)
          }
        ];

        return (
          <Dropdown
            menu={{ items: actionItems }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="text"
              icon={<MoreOutlined style={{ fontSize: 18 }} />}
              style={{
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            />
          </Dropdown>
        );
      }
    },
  ];

  // Style des lignes alternées
  const rowClassName = (_, index) => {
    return index % 2 === 0 ? 'table-row-light' : 'table-row-dark';
  };

  return (
    <div className="content-wrapper">
      <style>
        {`
          .table-row-light {
            background-color: #ffffff;
          }
          .table-row-dark {
            background-color: #f0f5ff;
          }
          .ant-table-tbody > tr:hover > td {
            background-color: #e6f7ff !important;
          }
          .ant-table-thead > tr > th {
            background: #fafafa !important;
            color: #262626 !important;
            font-weight: 600 !important;
          }
          .stat-card-blue {
            background: linear-gradient(135deg, #1890ff 0%, #69c0ff 100%);
            border-radius: 12px;
            border: none;
          }
          .stat-card-green {
            background: linear-gradient(135deg, #52c41a 0%, #95de64 100%);
            border-radius: 12px;
            border: none;
          }
          .stat-card-purple {
            background: linear-gradient(135deg, #722ed1 0%, #9254de 100%);
            border-radius: 12px;
            border: none;
          }
          .stat-card-orange {
            background: linear-gradient(135deg, #fa8c16 0%, #ffc069 100%);
            border-radius: 12px;
            border: none;
          }
          .stat-card-blue .ant-statistic-title,
          .stat-card-green .ant-statistic-title,
          .stat-card-purple .ant-statistic-title,
          .stat-card-orange .ant-statistic-title {
            color: rgba(255,255,255,0.85) !important;
            font-size: 13px !important;
          }
          .stat-card-blue .ant-statistic-content,
          .stat-card-green .ant-statistic-content,
          .stat-card-purple .ant-statistic-content,
          .stat-card-orange .ant-statistic-content {
            color: white !important;
          }
        `}
      </style>

      <div className="container-xxl flex-grow-1" style={{ paddingTop: 8, paddingLeft: 24, paddingRight: 24 }}>
        {/* Breadcrumb */}
        <Breadcrumb
          separator={<RightOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />}
          style={{ marginBottom: 12, marginTop: 0, paddingTop: 0 }}
        >
          <Breadcrumb.Item>
            <Link to="/dashboard" style={{ color: '#1890ff' }}>Accueil</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/dashboard" style={{ color: '#1890ff' }}>Inscription</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <span style={{ color: '#595959' }}>Véhicules</span>
          </Breadcrumb.Item>
        </Breadcrumb>

        {/* Statistiques */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={12} md={6}>
            <Card className="stat-card-blue" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
              <Statistic
                title={<span><CarOutlined style={{ marginRight: 6 }} />Total véhicules</span>}
                value={stats.total}
                valueStyle={{ fontSize: isMobile ? 20 : 28, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card className="stat-card-green" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
              <Statistic
                title={<span><TagOutlined style={{ marginRight: 6 }} />Catégories</span>}
                value={stats.categories}
                valueStyle={{ fontSize: isMobile ? 20 : 28, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card className="stat-card-purple" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
              <Statistic
                title={<span><CalendarOutlined style={{ marginRight: 6 }} />Ce mois</span>}
                value={stats.thisMonth}
                valueStyle={{ fontSize: isMobile ? 20 : 28, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card className="stat-card-orange" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
              <Statistic
                title={<span><AppstoreOutlined style={{ marginRight: 6 }} />Aujourd'hui</span>}
                value={stats.today}
                valueStyle={{ fontSize: isMobile ? 20 : 28, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Carte principale */}
        <Card
          style={{
            borderRadius: 16,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}
          bodyStyle={{ padding: isMobile ? 12 : 24 }}
        >
          {/* Filtres */}
          <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
            <Col xs={24} sm={12} md={7}>
              <Input
                prefix={<SearchOutlined style={{ color: '#1890ff' }} />}
                placeholder="Rechercher marque, modèle, immatriculation..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                allowClear
                size={controlSize}
                style={{ borderRadius: 8 }}
              />
            </Col>
            <Col xs={24} sm={12} md={5}>
              <Select
                placeholder="Filtrer par ville"
                value={selectedCity}
                onChange={(value) => setSelectedCity(value)}
                allowClear
                size={controlSize}
                style={{ width: '100%', borderRadius: 8 }}
                suffixIcon={<GlobalOutlined style={{ color: '#1890ff' }} />}
              >
                {cities.map(city => (
                  <Select.Option key={city._id} value={city._id}>
                    <GlobalOutlined style={{ marginRight: 6, color: '#1890ff' }} />
                    {city.name}
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <DatePicker
                onChange={(date) => setSelectedDate(date)}
                placeholder="Filtrer par date"
                format="DD/MM/YYYY"
                allowClear
                size={controlSize}
                style={{ width: '100%', borderRadius: 8 }}
              />
            </Col>
            <Col xs={24} sm={12} md={6} style={{ display: 'flex', justifyContent: isMobile ? 'flex-start' : 'flex-end' }}>
              <Dropdown
                menu={{ items: exportMenuItems }}
                placement="bottomRight"
                trigger={['click']}
              >
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  size={controlSize}
                  style={{
                    borderRadius: 8,
                    background: 'linear-gradient(135deg, #1890ff 0%, #69c0ff 100%)',
                    border: 'none'
                  }}
                >
                  Exporter {!isMobile && `(${filteredData.length})`}
                </Button>
              </Dropdown>
            </Col>
          </Row>

          {/* Table */}
          <Table
            columns={columns}
            dataSource={filteredData.map((item) => ({
              ...item,
              key: item._id,
            }))}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: [5, 10, 20, 50, 100],
              showTotal: (t, range) => (
                <span style={{ color: '#8c8c8c' }}>
                  {range[0]}-{range[1]} sur {t} véhicules
                </span>
              ),
              showLessItems: isMobile
            }}
            scroll={{ x: 800 }}
            size={controlSize}
            loading={isLoading}
            rowClassName={rowClassName}
          />
        </Card>
      </div>
    </div>
  );
};

export default Vehicules;
